import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/blog/Header';
import Footer from '../components/blog/Footer';
import PostCard from '../components/blog/PostCard';
import CategoryNav from '../components/blog/CategoryNav';
import { Container } from '../components/ui/Container';
import Pagination from '../components/ui/Pagination';
import Spinner from '../components/ui/Spinner';
import { usePagination } from '../hooks/usePagination';
import SEO from '../components/seo/SEO';
import { useRegionalSeo } from '../hooks/useRegionalSeo';
import { useSeoExtras } from '../context/SiteSeoContext';
import { fetchTrendingArticles } from '../store/actions/articleActions';
import { fetchCategories } from '../store/actions/categoryActions';
import { transformArticles, transformCategories } from '../utils/transformData';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background};
`;

const Main = styled.main`
  ${Container} {
    padding-top: 2rem;
    padding-bottom: 2rem;
    
    @media (min-width: 768px) {
      padding-top: 3rem;
      padding-bottom: 3rem;
    }
    
    @media (min-width: 1024px) {
      padding-top: 4rem;
      padding-bottom: 4rem;
    }
  }
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const PageTitle = styled.h1`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 2rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin-bottom: 0.75rem;
  
  @media (min-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 3rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 3.5rem;
  }
`;

const PageDescription = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.colors.mutedForeground};
  max-width: 42rem;
  line-height: 1.7;
  
  @media (min-width: 768px) {
    font-size: 1.125rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 1.25rem;
  }
`;

const TimeFilter = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  
  @media (min-width: 768px) {
    margin-bottom: 2.5rem;
  }
`;

const TimeFilterButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: ${props => props.theme.borderRadius.lg};
  background-color: ${props => props.$active ? props.theme.colors.primary : props.theme.colors.secondary};
  color: ${props => props.$active ? props.theme.colors.primaryForeground : props.theme.colors.foreground};
  border: none;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  @media (min-width: 1024px) {
    padding: 0.625rem 1.25rem;
    font-size: 0.9375rem;
  }
  
  &:hover {
    background-color: ${props => props.$active ? props.theme.colors.primary : props.theme.colors.muted};
  }
`;

const PostsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  margin-top: 1.5rem;
  margin-bottom: 3rem;
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.5rem;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.75rem;
    margin-bottom: 4rem;
  }
  
  @media (min-width: 1440px) {
    gap: 2rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 0;
  
  p {
    color: ${props => props.theme.colors.mutedForeground};
    font-size: 1rem;
    
    @media (min-width: 1024px) {
      font-size: 1.125rem;
    }
  }
`;

const Trending = () => {
  const dispatch = useDispatch();
  const { language, apiRegion } = useLanguage();
  // Use language from context (set based on region from URL)
  const activeLanguage = language;
  const [activeCategory, setActiveCategory] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');

  const { trendingArticles: trendingArticlesFromStore, loading: articlesLoading } = useSelector(state => state.article);
  const { categories: categoriesFromStore, loading: categoriesLoading } = useSelector(state => state.category);

  useEffect(() => {
    dispatch(fetchTrendingArticles(50, { lang: activeLanguage, region: apiRegion }));
    dispatch(fetchCategories({ lang: activeLanguage, region: apiRegion }));
  }, [dispatch, activeLanguage, apiRegion]);

  const trendingArticles = transformArticles(trendingArticlesFromStore);
  const categories = transformCategories(categoriesFromStore);
  
  // Filter by category
  const filteredByCategory = useMemo(() => {
    return activeCategory === 'all'
      ? trendingArticles
      : trendingArticles.filter(post => 
          post.category?.toLowerCase() === activeCategory.toLowerCase() ||
          post.category === activeCategory
        );
  }, [activeCategory, trendingArticles]);

  const { paginatedItems, currentPage, totalPages, goToPage } = usePagination(filteredByCategory, 6);

  const regionalSeo = useRegionalSeo('/trending');
  const seoExtras = useSeoExtras();

  const loading = articlesLoading || categoriesLoading;

  return (
    <PageWrapper>
      <SEO
        title="Trending Articles"
        description="Discover the most popular and engaging articles that our community is reading right now."
        url={regionalSeo.pageUrl}
        language={regionalSeo.language}
        availableLanguages={regionalSeo.availableLanguages}
        alternateUrls={regionalSeo.alternateUrls}
        {...seoExtras}
      />
      <Header />
      <Main>
        <Container>
          <PageHeader>
            <PageTitle>Trending Articles</PageTitle>
            <PageDescription>
              Discover the most popular and engaging articles that our community is reading right now.
            </PageDescription>
          </PageHeader>

          <TimeFilter>
            <TimeFilterButton
              $active={timeFilter === 'all'}
              onClick={() => setTimeFilter('all')}
            >
              All Time
            </TimeFilterButton>
            <TimeFilterButton
              $active={timeFilter === 'today'}
              onClick={() => setTimeFilter('today')}
            >
              Today
            </TimeFilterButton>
            <TimeFilterButton
              $active={timeFilter === 'week'}
              onClick={() => setTimeFilter('week')}
            >
              This Week
            </TimeFilterButton>
            <TimeFilterButton
              $active={timeFilter === 'month'}
              onClick={() => setTimeFilter('month')}
            >
              This Month
            </TimeFilterButton>
          </TimeFilter>

          {!categoriesLoading && (
            <CategoryNav
              categories={categories}
              activeCategory={activeCategory}
              onCategoryChange={setActiveCategory}
            />
          )}

          {loading ? (
            <Spinner />
          ) : filteredByCategory.length > 0 ? (
            <>
              <PostsGrid>
                {paginatedItems.map((post, index) => (
                  <PostCard
                    key={post._id || `${post.title}-${index}`}
                    {...post}
                    index={index}
                  />
                ))}
              </PostsGrid>
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={goToPage}
                />
              )}
            </>
          ) : (
            <EmptyState>
              <p>No trending articles found.</p>
            </EmptyState>
          )}
        </Container>
      </Main>
      <Footer />
    </PageWrapper>
  );
};

export default Trending;

